import { Directive, ElementRef, NgZone, OnDestroy, AfterViewInit, effect, inject, input } from '@angular/core';

/** Never shrink below this, so text stays legible; past it the text wraps as before. */
const MIN_FONT_PX = 6;

/**
 * Text always fits its box: when the text (a live value such as "$11,432,602")
 * is wider or taller than the layer, its font size is lowered just enough for it
 * to fit; text that already fits keeps the size the designer chose.
 *
 * A word is never split to make it fit (a number broke into "$11,432," / "602"
 * and spilled out of its pill) - it shrinks instead. Re-checked whenever the
 * text, the box size, the layer's style or the loaded fonts change. Works on the
 * canvas, in Preview and on published screens (they all render this element).
 */
@Directive({
  selector: '[dsgFitText]',
  standalone: true,
})
export class DesignFitTextDirective implements AfterViewInit, OnDestroy {
  /** Off while the text is being edited in place (the caret needs the real size). */
  readonly dsgFitText = input<boolean>(true);

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);
  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;
  private frame = 0;
  private readonly onFonts = () => this.schedule();

  constructor() {
    effect(() => {
      this.dsgFitText();
      this.schedule();
    });
  }

  ngAfterViewInit(): void {
    const element = this.host.nativeElement;
    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => this.schedule());
      this.resizeObserver.observe(element);
      this.mutationObserver = new MutationObserver(() => this.schedule());
      this.mutationObserver.observe(element, { characterData: true, childList: true, subtree: true });
      // The layer's font / size live on the parent node element's style.
      if (element.parentElement) {
        this.mutationObserver.observe(element.parentElement, { attributes: true, attributeFilter: ['style'] });
      }
      document.fonts?.addEventListener?.('loadingdone', this.onFonts);
    });
    this.schedule();
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.frame);
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    document.fonts?.removeEventListener?.('loadingdone', this.onFonts);
  }

  private schedule(): void {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.fit();
    });
  }

  private fit(): void {
    const element = this.host.nativeElement;
    // Our own style writes must not re-trigger the observer into a loop.
    this.mutationObserver?.takeRecords();
    element.style.fontSize = '';
    element.style.overflowWrap = '';
    element.style.wordBreak = '';
    if (!this.dsgFitText() || !element.isConnected || !element.clientWidth || !element.clientHeight) return;

    const base = parseFloat(getComputedStyle(element).fontSize);
    if (!Number.isFinite(base) || base <= 0) return;

    // Measure with words kept whole, so a too-long word shows up as overflow.
    element.style.overflowWrap = 'normal';
    element.style.wordBreak = 'normal';
    if (this.fits(element)) {
      this.restoreWrapping(element);
      return;
    }

    const min = Math.min(MIN_FONT_PX, base);
    let low = min;
    let high = base;
    element.style.fontSize = `${min}px`;
    if (!this.fits(element)) {
      // Even the smallest size overflows: fall back to the designer's size and normal wrapping.
      element.style.fontSize = '';
      this.restoreWrapping(element);
      this.mutationObserver?.takeRecords();
      return;
    }
    // Largest size that fits, to a tenth of a pixel.
    for (let i = 0; i < 12 && high - low > 0.1; i++) {
      const mid = (low + high) / 2;
      element.style.fontSize = `${mid}px`;
      if (this.fits(element)) low = mid;
      else high = mid;
    }
    element.style.fontSize = `${Math.floor(low * 10) / 10}px`;
    this.mutationObserver?.takeRecords();
  }

  private fits(element: HTMLElement): boolean {
    return element.scrollWidth <= element.clientWidth + 1 && element.scrollHeight <= element.clientHeight + 1;
  }

  private restoreWrapping(element: HTMLElement): void {
    element.style.overflowWrap = '';
    element.style.wordBreak = '';
  }
}
